// src/pages/PharmacyReportPage.jsx
import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function PharmacyReportPage() {
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [index, setIndex] = useState(0);
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");

  /* ---------------------- 1️⃣ Načtení seznamu verzí ---------------------- */
  useEffect(() => {
    if (!user?.firma) return;

    (async () => {
      try {
        const res = await fetch(
          `/api/zmeny-uhrady/list/${user.firma}?pharmacyCode=${user.pharmacyCode}`,
          { credentials: "include" }
        );

        if (!res.ok) throw new Error("Chyba při načítání seznamu");

        const json = await res.json();
        setVersions(json || []);
      } catch (e) {
        console.error(e);
        setErr("Nelze načíst seznam verzí");
      }
    })();
  }, [user]);

  /* ---------------------- 2️⃣ Načtení PDF ---------------------- */
  useEffect(() => {
    if (!user || !versions.length) return;

    const version = versions[index];
    if (!version) return;

    let url = "";

    (async () => {
      try {
        const res = await fetch(
          `/api/zmeny-uhrady/pdf/${version._id}/${user.firma}/${user.pharmacyCode}`,
          { credentials: "include" }
        );

        if (!res.ok) throw new Error("Chyba PDF");

        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        console.error(e);
        setErr("Nelze načíst PDF");
      }
    })();

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [index, user, versions]);

  const current = versions[index];
  const created = current ? new Date(current.createdAt) : null;
  const batch = current?.lastBatch ? new Date(current.lastBatch) : null;

  const dateSource =
    batch && created && batch < created ? batch : created;

  const formattedDate = dateSource
    ? dateSource.toLocaleString("cs-CZ", {
        timeZone: "Europe/Prague",
      }).replace(/\. /g, ".")
    : "";

  const prevDisabled = index >= versions.length - 1;
  const nextDisabled = index <= 0;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-blue-100">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-blue-900 flex items-center gap-3">
            Změny úhrad
          </h2>
          {current && (
            <div className="text-sm text-gray-600 mt-1">
              Poslední aktualizace: {formattedDate}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIndex((i) => i + 1)}
            disabled={prevDisabled}
            className={`p-2 rounded border ${
              prevDisabled
                ? "text-gray-400 border-gray-200"
                : "text-blue-700 hover:bg-blue-50 border-blue-200"
            }`}
          >
            <ArrowLeft size={18} />
          </button>
          <button
            onClick={() => setIndex((i) => i - 1)}
            disabled={nextDisabled}
            className={`p-2 rounded border ${
              nextDisabled
                ? "text-gray-400 border-gray-200"
                : "text-blue-700 hover:bg-blue-50 border-blue-200"
            }`}
          >
            <ArrowRight size={18} />
          </button>

          {blobUrl && (
            <a
              href={blobUrl}
              download={`zmeny_uhrad_${user?.pharmacyCode}.pdf`}
              className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-blue-200 hover:bg-blue-50"
            >
              <Download className="w-4 h-4" /> Stáhnout PDF
            </a>
          )}
        </div>
      </div>

      {err ? (
        <div className="p-4 text-red-600">{err}</div>
      ) : !blobUrl ? (
        <div className="p-4">Načítání…</div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-blue-100">
          <object
            data={blobUrl}
            type="application/pdf"
            className="w-full"
            style={{ height: "80vh" }}
          >
            <p className="p-4">
              Nelze zobrazit PDF.{" "}
              <a
                href={blobUrl}
                download={`zmeny_uhrad_${user?.pharmacyCode}.pdf`}
                className="text-blue-700 underline"
              >
                Stáhnout PDF
              </a>
            </p>
          </object>
        </div>
      )}
    </div>
  );
}
